import { CATEGORIES, CATEGORIESi18, Category, Language } from '@/config';
import type { Expense, Mileage, RouteDetails } from '@/types/types';

const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(' ');
};

const getStops = (route: RouteDetails | undefined) => {
  const defaultStops = [
    {
      id: 1,
      placeId: undefined,
      value: undefined,
    },
    {
      id: 2,
      placeId: undefined,
      value: undefined,
    },
  ];
  if (!route) {
    return defaultStops;
  }

  const { origin, destination, waypoints } = route;
  if (!origin || !destination) {
    return defaultStops;
  }
  if (waypoints.length === 0) {
    return [
      {
        id: 1,
        placeId: undefined,
        value: origin.description,
      },
      {
        id: 2,
        placeId: undefined,
        value: destination.description,
      },
    ];
  } else {
    return [
      {
        id: 1,
        placeId: undefined,
        value: origin.description,
      },
      {
        id: 2,
        placeId: undefined,
        value: waypoints[0]?.description,
      },
      {
        id: 3,
        placeId: undefined,
        value: destination.description,
      },
    ];
  }
};

const parseLanguage = (rawLng: string): Language => {
  switch (rawLng) {
    case 'en':
    case 'de':
      return rawLng; // Valid language, return as-is
    default:
      return 'en'; // Default to English if invalid
  }
};

function isDefaultCategory(str: string): str is Category {
  return CATEGORIES.includes(str as Category);
}

const getCategoryTitle = (item: Mileage | Expense, lng: string) => {
  const _lng = lng === 'de' ? 'de' : 'en';
  const isExpense = 'issuer' in item; // Check if the item is an Expense

  let title = '-';
  if (isExpense) {
    title = _lng === 'de' ? 'Sonstiges' : 'Others';
    if (item?.category_title) {
      if (isDefaultCategory(item.category_title)) {
        title = CATEGORIESi18[item.category_title][_lng];
      } else {
        title = item.category_title;
      }
    }
  } else {
    title = _lng === 'de' ? 'Spesen' : 'Expenses';
  }
  return title;
};

const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'CHF':
      return 'CHF';
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    default:
      return currency;
  }
};

const valueFormatter = (
  lng: string = 'en',
  number: number,
  currency?: string | undefined | null,
) => {
  return currency
    ? getCurrencySymbol(currency) +
        ' ' +
        new Intl.NumberFormat(lng === 'de' ? 'de-CH' : 'en-CH').format(number).toString()
    : new Intl.NumberFormat(lng === 'de' ? 'de-CH' : 'en-CH').format(number).toString();
};

const getYAxisWidth = (
  lng: string,
  chartData: { [key: string]: number | string }[],
  defaultCurrency: string,
) => {
  const maxValue = chartData.reduce((max, item) => {
    const numbers = Object.values(item).filter((value) => typeof value === 'number');
    return Math.max(max, ...numbers);
  }, 0);
  const length = valueFormatter(lng, maxValue, defaultCurrency ?? '').length;
  return length * 7 + 10;
};

const normalizeSlug = (text: string) => {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

/** Printed VAT rounding (Rundungsdifferenz) is typically 0.01; ignore smaller gaps. */
export const SPLIT_SUM_TOLERANCE = 0.05;

const toCents = (value: number): number => Math.round(value * 100);

export const splitsSumMatchesTotal = (total: number, splitsSum: number): boolean =>
  Number.isFinite(total) &&
  Number.isFinite(splitsSum) &&
  Math.abs(toCents(total) - toCents(splitsSum)) <= toCents(SPLIT_SUM_TOLERANCE);

export default {
  classNames,
  getStops,
  parseLanguage,
  getCategoryTitle,
  valueFormatter,
  getYAxisWidth,
  normalizeSlug,
};
